import Tabs from '@mui/material/Tabs'
import { path } from 'ramda'
import React, { PureComponent } from 'react'

import { AudioRecorderRef } from '@/app/component/atom/audio/recorder'
import TooltipTab from '@/app/component/atom/tooltip-tab'
import { createConfirmation } from '@/app/contexts/confirmation.context'
import AudioFileDialog from '@/app/module/campaigns/component/audio-file-dialog'
import { voiceFilterMap, whatsappFilterMap } from '@/app/module/campaigns/definitions'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import { Language, VoiceMessage, VoiceProps, VoiceQuestion } from '@/app/module/campaigns/types'
import { downloadFile } from '@/app/service/util/download'
import { PersonalizationType } from '@/app/types'

import ContentAudio from './content-audio'
import LanguageWrapper from './language-wrapper'
import TextToSpeech from './text-to-speech'
import { getAudioLanguageMap } from './types/multimessage/content'

type Props = Pick<
  VoiceProps,
  'files' | 'filterType' | 'loading' | 'playId' | 'getFile' | 'onMicAccessError' | 'saveFileHandler' | 'setPlaying'
> & {
  debounceChange?: boolean
  defaultLanguage: string
  enableTTS: boolean
  enableTranscription: boolean
  info: string
  item: Partial<VoiceMessage | VoiceQuestion>
  languages?: Language[]
  personalizationList: PersonalizationType
  voiceList?: BaseLanguageType[]

  changeHandler: (item: any) => void
  onRecordingChange?: (recording: boolean) => void
  onStop?: () => void
}

enum VoiceTab {
  Audio = 'playfile',
  TTS = 'say',
}

type State = {
  activeLanguage?: string
  downloading: boolean
  filter: string
  newFile: any
  playing: boolean
  recording: boolean
  selectExistingDialog: {
    language: string
    open: boolean
  }
  selectedVoice: string
  tab: VoiceTab
  uploading: boolean
}

export default class CampaignContentVoice extends PureComponent<Props, State> {
  state = {
    activeLanguage: '',
    downloading: false,
    filter: 'asIs',
    newFile: null,
    playing: false,
    recording: false,
    selectExistingDialog: {
      language: '',
      open: false,
    },
    selectedVoice: '',
    tab: VoiceTab.Audio,
    uploading: false,
  }

  recorderRef: React.RefObject<AudioRecorderRef>

  uploadRef: React.RefObject<any>

  constructor(props: Props) {
    super(props)
    this.recorderRef = React.createRef()
    this.uploadRef = React.createRef()

    this.state.selectedVoice = props.item.audio?.voice || ''
    this.state.tab = isTTS(props.item, props.languages) ? VoiceTab.TTS : VoiceTab.Audio
  }

  componentDidUpdate(_: Readonly<Props>, prevState: Readonly<State>): void {
    const { recording } = this.state
    const { onRecordingChange } = this.props

    if (!onRecordingChange || recording === prevState.recording) {
      return
    }

    onRecordingChange(recording)
  }

  componentWillUnmount() {
    this.props.onStop?.()
  }

  downloadAudioFile = async (url: string, file: string) => {
    this.setState({
      downloading: true,
    })
    try {
      await downloadFile(url, file, true)
    } finally {
      this.setState({
        downloading: false,
      })
    }
  }

  setDefaultContent = (tab: string) => {
    const { item, changeHandler } = this.props

    const defaultPlayfile = {
      playfile: '',
      transcription: '',
      translations: {},
    }
    const defaultSay = {
      say: '',
      transcription: '',
      translations: {},
    }

    changeHandler({
      ...item,
      audio: tab === VoiceTab.Audio ? defaultPlayfile : defaultSay,
    })
  }

  handleTabChange = async (tab: VoiceTab) => {
    const shouldConfirm = shouldConfirmSwitch(tab, this.props.item, this.props.languages)

    if (shouldConfirm) {
      const confirmed = await createConfirmation({
        title: 'Change Voice message type',
        content: 'Switching message types will discard your existing content. Do you want to continue?',
      })

      if (!confirmed) {
        return
      }

      this.handleMessageTypeSwitch(tab)
      return
    }

    this.setState(
      {
        tab,
      },
      () => {
        this.setDefaultContent(tab)
      },
    )
  }

  handleMessageTypeSwitch = (tab: VoiceTab) => {
    this.setState(
      {
        selectedVoice: '',
        tab,
      },
      () => {
        this.setDefaultContent(tab)
      },
    )
  }

  handleDownloadClick = async (audio: string) => {
    const file = await this.props.getFile(audio)
    if (file instanceof Error) {
      return
    }

    const { url } = file
    if (url) {
      this.downloadAudioFile(url, audio)
    }
  }

  handlePlayToggle = (val: boolean, audio: string) => {
    const { files, item, getFile, setPlaying } = this.props
    const id = item.id || ''

    setPlaying(val ? id : '')
    this.setState({
      playing: val,
    })
    if (val && !files?.[audio]?.url) {
      getFile(audio)
    }
  }

  handleRecordClick = async () => {
    if (this.state.recording) {
      await this.recorderRef.current?.stopRecording()
    } else {
      await this.recorderRef.current?.startRecording()
    }
    this.setState((s) => ({ recording: !s.recording }))
  }

  handleRecordFinish = async (newFile: File, activeLanguage: string) => {
    const { defaultLanguage, filterType, item, changeHandler, saveFileHandler } = this.props
    await this.recorderRef.current?.stopRecording()
    this.setState({ recording: false, uploading: true })
    try {
      const res = await saveFileHandler({
        file: newFile,
        filename: newFile.name,
        filter: filterMap('record', filterType),
        type: 'audio',
      })
      if (res instanceof Error) {
        return
      }
      if (!activeLanguage || activeLanguage === defaultLanguage) {
        changeHandler({
          ...item,
          audio: {
            ...item.audio,
            playfile: res.filename,
            transcription: '',
          },
        })
        return
      }

      changeHandler({
        ...item,
        audio: {
          ...item.audio,
          translations: {
            ...item.audio?.translations,
            [activeLanguage]: {
              ...item.audio?.translations?.[activeLanguage],
              playfile: res.filename,
              transcription: '',
            },
          },
        },
      })
    } finally {
      this.setState({ uploading: false })
    }
  }

  handleUpload = async (newFile: File, activeLanguage: string) => {
    const { defaultLanguage, filterType, item, changeHandler, saveFileHandler } = this.props
    this.setState({
      uploading: true,
    })
    try {
      const res = await saveFileHandler({
        file: newFile,
        filename: newFile.name,
        filter: filterMap(this.state.filter, filterType),
        type: 'audio',
      })
      if (res instanceof Error) {
        return
      }
      if (!activeLanguage || activeLanguage === defaultLanguage) {
        changeHandler({
          ...item,
          audio: {
            ...item.audio,
            playfile: res.filename,
            transcription: '',
          },
        })
        return
      }

      changeHandler({
        ...item,
        audio: {
          ...item.audio,
          translations: {
            ...item.audio?.translations,
            [activeLanguage]: {
              ...item.audio?.translations?.[activeLanguage],
              playfile: res.filename,
              transcription: '',
            },
          },
        },
      })
    } finally {
      this.setState({
        uploading: false,
      })
    }
  }

  handleSelectExistingOpen = (language: string) => {
    this.setState({
      selectExistingDialog: {
        language: language || '',
        open: true,
      },
    })
  }

  handleSelectExistingClose = () => {
    this.setState({
      selectExistingDialog: {
        language: '',
        open: false,
      },
    })
  }

  handleSelectExistingSave = (filename: string) => {
    const { defaultLanguage, files, item, changeHandler } = this.props
    const {
      selectExistingDialog: { language },
    } = this.state
    const transcription = files?.[filename]?.transcription || ''

    if (!language || language === defaultLanguage) {
      changeHandler({
        ...item,
        audio: {
          ...item.audio,
          playfile: filename,
          transcription,
        },
      })
    } else {
      changeHandler({
        ...item,
        audio: {
          ...item.audio,
          translations: {
            ...item.audio?.translations,
            [language]: {
              ...item.audio?.translations?.[language],
              playfile: filename,
              transcription,
            },
          },
        },
      })
    }

    this.setState({
      selectExistingDialog: {
        language: '',
        open: false,
      },
    })
  }

  handleTranscriptChange = (transcription: string, activeLanguage: string) => {
    const { defaultLanguage, item, changeHandler } = this.props

    if (!activeLanguage || activeLanguage === defaultLanguage) {
      return changeHandler({
        ...item,
        audio: {
          ...item.audio,
          transcription,
        },
      })
    }

    return changeHandler({
      ...item,
      audio: {
        ...item.audio,
        translations: {
          ...item.audio?.translations,
          [activeLanguage]: {
            ...item.audio?.translations?.[activeLanguage],
            transcription,
          },
        },
      },
    })
  }

  handleAudioDelete = (activeLanguage: string) => {
    const { defaultLanguage, item, changeHandler } = this.props

    if (!activeLanguage || activeLanguage === defaultLanguage) {
      return changeHandler({
        ...item,
        audio: {
          ...item.audio,
          playfile: '',
          transcription: '',
        },
      })
    }

    return changeHandler({
      ...item,
      audio: {
        ...item.audio,
        translations: {
          ...item.audio?.translations,
          [activeLanguage]: {
            ...item.audio?.translations?.[activeLanguage],
            playfile: '',
            transcription: '',
          },
        },
      },
    })
  }

  renderAudio = (audio: string, activeLanguage: string) => {
    const {
      defaultLanguage,
      enableTTS,
      enableTranscription,
      files,
      item,
      loading,
      playId,
      getFile,
      onMicAccessError,
      setPlaying,
    } = this.props
    const { downloading, recording, uploading } = this.state
    const id = item.id || ''
    const file = audio ? files?.[audio] : undefined

    return (
      <ContentAudio
        audio={audio}
        audioFile={file}
        downloading={downloading}
        enableTTS={enableTTS}
        enableTranscription={enableTranscription}
        id={id}
        loading={loading}
        playId={playId || ''}
        recorderRef={this.recorderRef}
        recording={recording}
        transcription={getTranscription(item, activeLanguage, defaultLanguage)}
        uploadRef={this.uploadRef}
        uploading={uploading}
        onDelete={() => this.handleAudioDelete(activeLanguage)}
        onDownload={() => this.handleDownloadClick(audio)}
        onPlayToggle={(val: boolean) => {
          setPlaying(val ? item.id || '' : '')
          this.setState({
            playing: val,
          })
          if (val && !path([audio, 'url'], files)) {
            getFile(audio)
          }
        }}
        onRecordClick={this.handleRecordClick}
        onRecordDisabled={() => {
          this.setState({
            recording: false,
          })
          onMicAccessError()
        }}
        onRecordFinish={(newFile: File) => this.handleRecordFinish(newFile, activeLanguage)}
        onSelectExisting={() => this.handleSelectExistingOpen(activeLanguage)}
        onStop={() => this.setState({ playing: false }, () => this.props.onStop?.())}
        onTranscriptChange={(transcription) => this.handleTranscriptChange(transcription, activeLanguage)}
        onUpload={(newFile: File) => this.handleUpload(newFile, activeLanguage)}
        onUploadStart={(filter: string) => {
          this.setState({
            filter,
          })
          this.uploadRef.current?.open()
        }}
      />
    )
  }

  handleLanguageChange = (language: string) => {
    const { defaultLanguage, item } = this.props

    const isDefault = language === defaultLanguage
    if (isDefault) {
      this.setState({ selectedVoice: item.audio?.voice || '' })
    } else {
      this.setState({ selectedVoice: item.audio?.translations?.[language]?.voice || '' })
    }
  }

  handleVoiceChange = (voice: string) => {
    this.setState({
      selectedVoice: voice,
    })
  }

  getTTSTabTooltip = () => {
    const { downloading, recording, playing, uploading } = this.state

    if (recording) {
      return 'Please stop recording before switching to Text-to-Speech.'
    }
    if (playing) {
      return 'Please stop playing the audio before switching to Text-to-Speech.'
    }
    if (downloading) {
      return 'Please wait for the download to finish before switching to Text-to-Speech.'
    }
    if (uploading) {
      return 'Please wait for the upload to finish before switching to Text-to-Speech.'
    }
    return ''
  }

  render() {
    const {
      debounceChange,
      defaultLanguage,
      enableTTS,
      info,
      item,
      languages,
      personalizationList,
      voiceList,
      changeHandler,
    } = this.props
    const { downloading, playing, recording, selectExistingDialog, selectedVoice, tab, uploading } = this.state
    const selectedFilename = getAudioFilename(item, selectExistingDialog.language, defaultLanguage)

    return (
      <>
        <LanguageWrapper
          content={tab as any}
          disabled={recording || playing}
          info={info}
          defaultLanguage={defaultLanguage}
          item={item}
          languageContentMap={item?.audio ? getAudioLanguageMap(item.audio, defaultLanguage) : {}}
          languages={languages}
          selectedVoice={selectedVoice}
          type="audio"
          onChange={changeHandler}
          onLanguageChange={this.handleLanguageChange}
        >
          {(
            content: string,
            onContentChange: (content: { message: string }) => void,
            error: string,
            activeLanguage: string,
          ) => {
            if (!enableTTS) {
              return this.renderAudio(content, activeLanguage)
            }

            return (
              <div data-testid="campaign-content-voice-tabs">
                <Tabs indicatorColor="primary" value={tab} onChange={(_, t) => this.handleTabChange(t)}>
                  <TooltipTab label="Audio" style={{ zIndex: 2 }} value="playfile" wrapped />
                  <TooltipTab
                    disabled={downloading || recording || playing || uploading}
                    label="Text-to-Speech"
                    style={{ zIndex: 2 }}
                    tooltip={this.getTTSTabTooltip()}
                    value="say"
                  />
                </Tabs>
                {tab === VoiceTab.Audio && <>{this.renderAudio(content, activeLanguage)}</>}
                {tab === VoiceTab.TTS && (
                  <TextToSpeech
                    activeLanguage={activeLanguage}
                    autoFocus={item.autoFocus}
                    debounceChange={debounceChange}
                    message={content}
                    personalizationList={personalizationList}
                    selectedVoice={selectedVoice}
                    voiceList={voiceList || []}
                    onChange={onContentChange}
                    onVoiceChange={this.handleVoiceChange}
                  />
                )}
              </div>
            )
          }}
        </LanguageWrapper>
        <AudioFileDialog
          open={selectExistingDialog.open}
          selectedFilename={selectedFilename}
          onClose={this.handleSelectExistingClose}
          onSave={this.handleSelectExistingSave}
        />
      </>
    )
  }
}

const filterMap = (filter: string, filterType: string) => {
  if (filterType === 'whatsapp') {
    return whatsappFilterMap[filter]
  }
  return voiceFilterMap[filter]
}

export const isTTS = (item: Partial<VoiceMessage | VoiceQuestion>, languages?: Language[]) => {
  if (item.audio?.playfile) {
    // if the default language has playfile attribute, then it's not TTS
    return false
  }

  const hasTTSAttribute = item.audio?.say !== undefined || item.audio?.voice !== undefined
  if (!languages?.length) {
    // if there are no languages, then it's TTS if the default language has say or voice attribute
    return hasTTSAttribute
  }

  for (let i = 0; i < languages.length; i += 1) {
    const language = languages[i]

    // if any of the languages has playfile attribute, then it's not TTS
    if (item.audio?.translations?.[language.value]?.playfile) {
      return false
    }

    if (
      item.audio?.translations?.[language.value]?.say !== undefined ||
      item.audio?.translations?.[language.value]?.voice !== undefined
    ) {
      return true
    }
  }

  return hasTTSAttribute
}

export const getAudioFilename = (
  item: Partial<VoiceMessage | VoiceQuestion>,
  activeLanguage: string,
  defaultLanguage: string,
) => {
  if (!activeLanguage || activeLanguage === defaultLanguage) {
    return item.audio?.playfile || ''
  }

  return item.audio?.translations?.[activeLanguage]?.playfile || ''
}

export const getTranscription = (
  item: Partial<VoiceMessage | VoiceQuestion>,
  activeLanguage: string,
  defaultLanguage: string,
) => {
  if (!activeLanguage || activeLanguage === defaultLanguage) {
    return item.audio?.transcription || ''
  }

  return item.audio?.translations?.[activeLanguage]?.transcription || ''
}

export const shouldConfirmSwitch = (
  tab: VoiceTab,
  item: Partial<VoiceMessage | VoiceQuestion>,
  languages?: Language[],
) => {
  if (tab === VoiceTab.Audio) {
    if (item.audio?.say || item.audio?.voice) {
      return true
    }

    if (languages?.length) {
      for (let i = 0; i < languages.length; i += 1) {
        const language = languages[i]
        const translation = item.audio?.translations?.[language.value]

        if (translation?.say || translation?.voice) {
          return true
        }
      }
    }

    return false
  }

  if (tab === VoiceTab.TTS) {
    if (item.audio?.playfile) {
      return true
    }

    if (languages?.length) {
      for (let i = 0; i < languages.length; i += 1) {
        const language = languages[i]
        const translation = item.audio?.translations?.[language.value]

        if (translation?.playfile) {
          return true
        }
      }
    }

    return false
  }

  return false
}
